/**
 * 全局公司/品牌/产品上下文状态管理 — Zustand store + persist 中间件。
 *
 * 管理当前选中的公司/品牌/产品 ID，跨页面共享。
 * 仅持久化 selectedCompanyId / selectedBrandId / selectedProductId 到 localStorage，
 * companies 列表和 isLocked 每次登录后重新初始化。
 *
 * 用法约定：
 * - 页面只读取 selectedCompanyId / selectedProductId，不直接修改（修改由 Topbar 选择器负责）
 * - 页面 useEffect 依赖数组必须包含 selectedCompanyId，确保切换公司时数据刷新
 * - 登录后调用 initFromAccount(account) 初始化
 * - 登出时调用 reset() 清空
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Account, Company } from "../types";

/** 公司上下文状态接口。 */
interface CompanyState {
  // ===== 状态 =====
  /** 可访问的公司列表（每次登录重新初始化，不持久化）。 */
  companies: Company[];
  /** 当前选中的公司 ID。 */
  selectedCompanyId: string;
  /** 当前选中的品牌 ID。 */
  selectedBrandId: string;
  /** 当前选中的产品 ID。 */
  selectedProductId: string;
  /** 公司用户 = true（锁定公司选择器），admin = false（可切换）。 */
  isLocked: boolean;

  // ===== Actions =====
  /** 设置公司列表。 */
  setCompanies: (list: Company[]) => void;
  /** 切换公司，同时重置品牌/产品选中。 */
  selectCompany: (id: string) => void;
  /** 切换品牌，同时重置产品选中。 */
  selectBrand: (id: string) => void;
  /** 切换产品。 */
  selectProduct: (id: string) => void;
  /** 登录后初始化公司上下文：admin 可选所有公司，公司用户锁定自己公司。 */
  initFromAccount: (account: Account) => void;
  /** 登出时清空所有状态。 */
  reset: () => void;
}

/** 默认初始状态。 */
const initialState = {
  companies: [] as Company[],
  selectedCompanyId: "",
  selectedBrandId: "",
  selectedProductId: "",
  isLocked: false,
};

export const useCompanyStore = create<CompanyState>()(
  persist(
    (set) => ({
      ...initialState,

      setCompanies: (list: Company[]) => {
        set({ companies: list });
      },

      selectCompany: (id: string) => {
        // 切换公司时重置品牌和产品
        set({
          selectedCompanyId: id,
          selectedBrandId: "",
          selectedProductId: "",
        });
      },

      selectBrand: (id: string) => {
        // 切换品牌时重置产品
        set({ selectedBrandId: id, selectedProductId: "" });
      },

      selectProduct: (id: string) => {
        set({ selectedProductId: id });
      },

      initFromAccount: (account: Account) => {
        // 运营方（operator）与 admin 同为可切换角色：可见集合含自己+直线下属公司；
        // 仅企业客户（enterprise_client）锁定自己公司。
        if (account.role === "admin" || account.account_type === "operator") {
          set({ isLocked: false });
        } else {
          // 公司用户：锁定为 account.company_id
          set({
            isLocked: true,
            selectedCompanyId: account.company_id || "",
            selectedBrandId: "",
            selectedProductId: "",
          });
        }
      },

      reset: () => {
        set({ ...initialState });
      },
    }),
    {
      name: "geo_company_context",
      // 仅持久化三个 selected ID，companies 和 isLocked 不持久化
      partialize: (state) => ({
        selectedCompanyId: state.selectedCompanyId,
        selectedBrandId: state.selectedBrandId,
        selectedProductId: state.selectedProductId,
      }),
    },
  ),
);
